import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router  } from '@angular/router';
import { GAHAppResponse } from '@data/schema/gah-app-response';
import { ReleaseInfo } from '@data/schema/release-info';
import { User } from '@data/schema/user-state';
import { AuthenticationService } from '@gah-core/services/authentication.service';
import { UserStoreService } from '@gah-core/services/user-store.service';
import { ManageHomepageService } from '@modules/admin/manage-homepage/services/manage-homepage.service';
import { GoogleanalyticsmoduleserviceService } from '@shared/services/googleanalyticsmoduleservice.service';
import { NgDynamicBreadcrumbService } from 'ng-dynamic-breadcrumb';
import { NGXLogger } from 'ngx-logger';
import { Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import SwiperCore, { SwiperOptions, Pagination, Navigation, Autoplay } from 'swiper';
import { EventsParams, SwiperComponent } from 'swiper/angular';
import { HomeFeaturedTitles } from '../models/home-featured-titles.model';
import { HomeTitles } from '../models/home-titles.model';
import { HomeService } from '../services/home.service';

SwiperCore.use([Pagination, Navigation, Autoplay]);

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit, OnDestroy {
  @ViewChild('singleItemSwiper', { static: false }) swiper?: SwiperComponent;
  singleItemSwiperConfig: SwiperOptions = {
    slidesPerView: 1,
    centeredSlides: true,
    centerInsufficientSlides: false,
    speed: 1000,
    loop: true,
  };
  multipleItemSwiperConfig: SwiperOptions = {
    spaceBetween: 10,
    centerInsufficientSlides: false,
    slidesPerView: 1, // Using breakpoints to increase them
  };
  territoryId: number = null;
  territoryName: string = 'Home';
  groupTypeCode: string = null;
  assetIds: Array<number> = [10610106];
  onReleaseTitles: HomeTitles[] = [];
  upcomingTitles: HomeTitles[] = [];
  recentUploadedAssetsTitles: HomeTitles[] = [];
  featuredTitles: HomeFeaturedTitles[] = [];
  releaseInfo: ReleaseInfo[] = [];
  releaseInfoUpcoming: ReleaseInfo[] = [];
  fetchedHomePageTitles: boolean = false;
  navigationSubscription: Subscription;
  showNoResults: boolean = false;
  showFT: boolean = true;
  showRT: boolean = true;
  showUT: boolean = true;
  showNAT: boolean = true;
  counter: number = 5;
  groupTerritoryId: number;
  
  constructor(
    private homeService: HomeService,
    private log: NGXLogger,
    private userStoreService: UserStoreService,
    private router: Router,
    private ngDynamicBreadcrumbService: NgDynamicBreadcrumbService,
    public authService: AuthenticationService,
    private homepageService: ManageHomepageService,
    private googleanalyticsmoduleserviceService: GoogleanalyticsmoduleserviceService
  ) {}

  ngOnInit(): void {
    if (this.userStoreService.userState.isLegalAccepted) {
      this.fetchReleaseSchedule();
      this.authService.getCartItems();
    }
    let intervalId = setInterval(() => {
      this.counter = this.counter - 1;
      if (this.counter === 0) {
        this.showFT = this.showUT = this.showRT = this.showNAT = false;
        clearInterval(intervalId);
      }
    }, 1000);
    // * Added to update the breadcrumb data on every route change
    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      this.ngDynamicBreadcrumbService.updateBreadcrumbLabels({
        territoryName: this.territoryName,
      });
    });
    // * Implemented this way to not make home page items request if user state is not updated yet.
    this.userStoreService.userState$.subscribe((userState: User) => {
      if (!userState?.selectedTerritory?.territoryId) return;
      this.territoryName = userState?.selectedTerritory?.territoryName;
      this.territoryId = userState?.selectedTerritory?.territoryId;
      this.groupTerritoryId = userState?.groupsTerritoryId;
      this.groupTypeCode = userState?.selectedTerritory?.groupTypeCode;
      this.ngDynamicBreadcrumbService.updateBreadcrumbLabels({
        territoryName: this.territoryName,
      });
      if (!this.fetchedHomePageTitles) {
        this.homeService
          .fetchHomePageReleaseData(this.territoryId, this.groupTypeCode)
          .pipe(first())
          .subscribe({
            next: (res: GAHAppResponse) => {
              this.showRT = this.showUT = false;
              this.showNoResults = true;
              this.onReleaseTitles = res?.serviceResult?.past;
              this.upcomingTitles = res?.serviceResult?.upcoming;
              this.fetchedHomePageTitles = true;
            },
            error: (err) => {
              this.showRT = this.showUT = false;
            },
          });

        this.homeService
          .fetchTitlesForRecentPublished(this.groupTerritoryId)
          .pipe(first())
          .subscribe({
            next: (res: GAHAppResponse) => {
              this.showNAT = false;
              this.showNoResults = true;
              this.recentUploadedAssetsTitles = res?.serviceResult;
            },
            error: (err) => {
              this.showNAT = false;
            },
          });

        this.homeService
          .fetchHomePageHeaderMediaData(this.userStoreService.userState.groupsTerritoryId)
          .pipe(first())
          .subscribe({
            next: (res: GAHAppResponse) => {
              this.showFT = false;
              this.showNoResults = true;
              let unsortedFeaturedTitles: HomeFeaturedTitles[] = res?.serviceResult;
              this.featuredTitles = unsortedFeaturedTitles?.sort((a, b) => {
                return a.displayOrder.valueOf() - b.displayOrder.valueOf();
              });
              this.fetchedHomePageTitles = true;
            },
            error: (err) => {
              this.showFT = false;
            },
          });
      }
    });
  }

  /**
   * This method handles swiper slide change event.
   * This resets video progress for active slide
   * and pauses auto slide transition till video finishes playing
   *
   * @param event - Swiper slide change event
   */
  swiperSlideChanged(event: EventsParams['activeIndexChange']) {
    let activeIndex = event[0]?.activeIndex;
    if (this.featuredTitles[activeIndex - 1]?.type === 'video') {
      let video: HTMLVideoElement =
        this.swiper?.swiperRef.slides[activeIndex].querySelector('video');
      if (video) {
        video.currentTime = 0;
        video.addEventListener('ended', () => {
          this.swiper?.swiperRef.autoplay.start();
          setTimeout(() => {
            this.swiper?.swiperRef.slideNext();
          }, 1000);
        });
        this.swiper?.swiperRef.autoplay.stop();
        video.play();
      }
    } else {
      this.swiper?.swiperRef.autoplay.start();
    }
  }

  /**
   * Function - Fetch Release Schedule
   */
  fetchReleaseSchedule() {
    if (
      this.authService.userState != null &&
      this.authService.userState.groupsTerritoryId != null &&
      this.authService.userState.username != null
    ) {
      this.homepageService
        .fetchUserGuide(
          this.authService.userState.groupsTerritoryId,
          this.authService.userState.username,
          'RS'
        )
        .subscribe();
    }
  }

  trackByFeatured(index: number, title: HomeFeaturedTitles) {
    return title.runnerId;
  }

  trackByUpcoming(index: number, title: HomeTitles) {
    return title.releaseId;
  }

  trackByOnRelease(index: number, title: HomeTitles) {
    return title.releaseId;
  }

  titleClick(event: any, title: HomeTitles) {
    this.router.navigateByUrl('/assets/' + title.titleId);
  }

  ngOnDestroy(): void {
    this.navigationSubscription?.unsubscribe();
  }
}
